import { apiStatus, flagKeys } from '@va/constants';
import { setUserFlag } from '@va/dashboard/actions/api';
import { addNotification, closeModal as closeModalAction } from '@va/dashboard/actions/ui';
import {
  getShortBusinessInfoSchema,
  ShortBusinessInfoForm,
  ShortBusinessInfoFormValues,
} from '@va/dashboard/components';
import { isModalOpen } from '@va/dashboard/selectors/ui';
import { CloseIconWithCircle } from '@va/icons';
import { useTranslate } from '@va/localization';
import { GET_AGENCY_CUSTOMER_REQUEST } from '@va/standalone/shared/actions';
import { createAgencyCustomer, updateAgencyCustomer } from '@va/standalone/shared/api-client/billing';
import { modalName } from '@va/standalone/shared/constants';
import { getUserId } from '@va/standalone/shared/helpers';
import { getApiRequest } from '@va/standalone/shared/selectors';
import { BusinessInfoType } from '@va/types/account';
import { Button, fontWeights, GrayGradientDivider, Heading5 } from '@va/ui/design-system';
import { ModalWrapper } from '@va/util/components';
import { FormikConfig, useFormik } from 'formik';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const FORM_ID = 'shortBusinessInfoForm';

export const ShortBusinessInfo = () => {
  const translate = useTranslate();
  const dispatch = useDispatch();
  const isOpen = useSelector((state) => isModalOpen(state, modalName.SHORT_BUSINESS_INFO));

  const agencyCustomerInfo = useSelector((state) =>
    getApiRequest(state, 'getAgencyCustomer'),
  ).toJS() as BusinessInfoType & { status: apiStatus };

  const formInitialValues = {
    companyName: agencyCustomerInfo.companyName || '',
    businessSector: agencyCustomerInfo.businessSector || '',
    businessType: agencyCustomerInfo.businessType || '',
    businessSize: agencyCustomerInfo.businessSize || '',
    doNotShowAgain: false,
  };

  const closeModal = useCallback(() => {
    dispatch(closeModalAction(modalName.SHORT_BUSINESS_INFO));
  }, [dispatch]);

  const handleFormSubmission = useCallback<FormikConfig<ShortBusinessInfoFormValues>['onSubmit']>(
    function ({ doNotShowAgain, ...values }, { setSubmitting }: { setSubmitting: (bool: boolean) => void }) {
      const userId = getUserId();
      const agencyId = agencyCustomerInfo.id;
      const agencyInfoCall = agencyId
        ? updateAgencyCustomer(userId, agencyCustomerInfo.id, values)
        : createAgencyCustomer(userId, values);
      agencyInfoCall
        .then(() => {
          setSubmitting(false);
          dispatch({ type: GET_AGENCY_CUSTOMER_REQUEST });
          closeModal();

          if (doNotShowAgain) {
            dispatch(setUserFlag(flagKeys.DO_NOT_SHOW_BUSINESS_INFO_MODAL, true));
          }
        })
        .catch(() => {
          setSubmitting(false);
          dispatch(
            addNotification({
              title: translate('notifications.generalError'),
              level: 'error',
              autoDismiss: 10,
            }),
          );
        });
    },
    [agencyCustomerInfo.id, closeModal, dispatch, translate],
  );

  const formikForm = useFormik<ShortBusinessInfoFormValues>({
    initialValues: formInitialValues,
    onSubmit: handleFormSubmission,
    validationSchema: getShortBusinessInfoSchema(),
    enableReinitialize: true,
  });

  const { isValid, isSubmitting, dirty } = formikForm;

  return (
    <ModalWrapper closeModal={closeModal} isModalOpen={isOpen}>
      <div className='w-[45rem] sm:w-fit overflow-hidden'>
        <div className='flex justify-between items-center p-9px pl-6'>
          <Heading5 className='text-wrap' colorClassName='text-gray-charcoal' weight={fontWeights.semibold}>
            {translate('modal.shortBusinessInfo.title')}
          </Heading5>
          <Button color='quinary' icon={() => <CloseIconWithCircle />} onClick={closeModal} />
        </div>
        <GrayGradientDivider />
        <ShortBusinessInfoForm id={FORM_ID} form={formikForm} />
        <GrayGradientDivider />
        <div className='p-3'>
          <Button
            className='w-full'
            text={translate('button.save')}
            disabled={!isValid || isSubmitting || !dirty}
            type='submit'
            form={FORM_ID}
          />
        </div>
      </div>
    </ModalWrapper>
  );
};
